import type { DamageZone, PhotoVerdict, ShotKey } from "./types"

// All user-facing strings live here so a PM/copywriter can adjust
// without grepping JSX. Every error blames the system, not the user.

export const VIN_COPY = {
  scanLabel: "Scan VIN",
  typeLabel: "Type VIN instead",
  scanHelpLabel: "Where do I find the VIN?",
  retakeLabel: "Retake photo",
  reading: "Reading VIN…",
  // One message — the user doesn't need to know whether it was OCR,
  // the decode service, or a partial result. Just retake or type.
  scanFailed: "Couldn't read it. Try again or type the VIN.",
  inputPlaceholder: "17-character VIN",
  inputHelp: "Numbers and letters only — no I, O, or Q.",
  editLabel: "Edit",
  // Quietly hint that license-plate scanning is on the roadmap so users
  // don't repeatedly try to point the camera at their plate today.
  plateTip: "Tip: license-plate scanning is coming soon — for now, scan the VIN itself.",
}

export const PHOTO_COPY = {
  rules: {
    standBack: "Stand back",
    wholeVehicle: "Whole vehicle in frame",
    goodLight: "Good light",
  },
  takePhoto: "Take Photo",
  retake: "Retake",
  skipShot: "Skip this shot",
  checking: "Checking…",
  next: "Next shot",
  goodEnough: "Good enough — we'll work with this one.",
  useThisPhoto: "Use this photo",
  tryAgain: "Retake again",
  closeUpsTitle: "Add damage close-ups?",
  closeUpsYes: "Yes, add close-ups",
  closeUpsNo: "No, I'm done",
  closeUpAdd: "Add another close-up",
  closeUpDone: "Done",
  closeUpFromCount: (n: number) => `Close-up #${n + 1}`,
  // Wrong-side correction — shown briefly during auto-advance
  // animation. Most users won't tap it; the few who need it have
  // a quick out so they don't get re-routed to a tagging step.
  wrongSidePrompt: "Wrong side?",
}

export const VERDICT_COPY: Record<PhotoVerdict, string> = {
  "good":         "Looking good — next shot",
  "too-close":    "Stand back further — car is too close",
  // Suggest the flashlight too — most users forget it's an option.
  "too-dark":     "Too dark — move to better light or turn on your phone's flashlight",
  "blurry":       "Blurry — hold the phone still and retake",
  "wrong-side":   "Looks like the wrong side — was this the front?",
  "low-quality":  "Hard to make out the vehicle — try again with the whole car in frame",
}

// Per-slot version of the wrong-side verdict, so the prompt actually
// names the angle the user was supposed to be on.
export function wrongSideCopy(expectedShot: ShotKey): string {
  const labels: Record<ShotKey, string> = {
    front: "the front",
    driver: "the driver side",
    passenger: "the passenger side",
    rear: "the rear",
  }
  return `Looks like the wrong side — was this ${labels[expectedShot]}?`
}

export const SEND_COPY = {
  title: "Last look",
  notesLabel: "Anything else for the body shop?",
  notesPlaceholder: "Optional — add any other detail the body shop should know.",
  // Tap-to-pick chips — first taps go into Notes, then user can edit.
  // Covers ~95% of incoming-damage scenarios; the textarea is fallback
  // for the rest.
  damageChips: [
    "Front-end collision",
    "Rear-end collision",
    "Side scrape",
    "Hail damage",
    "Vandalism",
    "Parking lot ding",
    "Cracked glass",
  ] as const,
  customerToggle: "Add customer info",
  customerNameLabel: "Customer name",
  customerPhoneLabel: "Customer phone",
  recentCustomerPrompt: "Use last customer",
  submit: "Send for Estimate",
  submitting: "Sending…",
  partialPhotosWarn: "You haven't captured all four sides. The body shop may ask for more.",
  successTitle: "Estimate submitted.",
  // Phone preview takes a callback so we can interpolate the number.
  successBodyWithPhone: (phone: string) =>
    `The body shop typically responds within 30 minutes. We'll text ${phone} when it's ready.`,
  successBodyNoPhone: "The body shop typically responds within 30 minutes. We'll send you a notification when it's ready.",
  successPrimary: "View estimate",
  successSecondary: "Done",
  failTitle: "Couldn't send the estimate.",
  failBody: "Try again — your photos and details are saved.",
  failPrimary: "Try again",
}

// Damage-zone tap-and-mark — labels the body shop sees.
export const DAMAGE_ZONE_COPY: Record<DamageZone, string> = {
  "hood": "Hood",
  "front-bumper": "Front bumper",
  "front-fender-left": "Front fender (driver)",
  "front-fender-right": "Front fender (passenger)",
  "front-door-left": "Front door (driver)",
  "front-door-right": "Front door (passenger)",
  "rear-door-left": "Rear door (driver)",
  "rear-door-right": "Rear door (passenger)",
  "quarter-panel-left": "Rear quarter (driver)",
  "quarter-panel-right": "Rear quarter (passenger)",
  "trunk": "Trunk",
  "rear-bumper": "Rear bumper",
  "roof": "Roof",
  "windshield": "Windshield",
}

export const DAMAGE_MARK_COPY = {
  eyebrow: "Step 2 of 3 · Mark damage",
  title: "Where's the damage?",
  blurb: "Tap any panel that's damaged. We'll tell the body shop exactly where to look — no typing needed.",
  noneSelected: "Skip if there's no specific damage to mark",
  continue: "Continue",
  zoneCount: (n: number) =>
    n === 0 ? "Tap any panel" : `${n} ${n === 1 ? "area" : "areas"} marked`,
}

export const RESUME_COPY = {
  title: "Pick up where you left off?",
  bodyMinutesAgo: (mins: number) =>
    mins < 1
      ? "You started an estimate moments ago."
      : mins < 60
        ? `You started an estimate ${mins} ${mins === 1 ? "minute" : "minutes"} ago.`
        : `You started an estimate ${Math.round(mins / 60)}h ago.`,
  resume: "Resume",
  startOver: "Start over",
}

// "Ship Now" affordance during photo capture — early-exit for users
// who got what they need before all 4 are captured.
export const SHIP_NOW_COPY = {
  label: "Ship what I have",
  // Sub-label appears when the user actually has fewer than 4 photos
  // when they tap, telling them what'll happen.
  subWhenPartial: (n: number) =>
    `Submit with ${n} of 4 photos — the body shop may ask for more.`,
}

// ── Consumer flow v2 (/go/:shopId, contact-first) ──
// Voiced for the car owner, not a shop employee. Contact is captured FIRST
// (the lead is what everything hangs off); every extra is skippable. No
// price appears anywhere before the shop's own estimate.
export const CONSUMER_V2 = {
  contact: {
    nameLabel: "Your name",
    phoneLabel: "Mobile number",
    phoneHint: "We text your estimate link here",
    emailLabel: "Email",
    emailOptionalLabel: "Email (optional)",
    emailTypoHint: (suggestion: string) => `Did you mean ${suggestion}?`,
    smsOptIn: "Text me updates about this request",
    cta: "Continue",
    ctaBusy: "Starting…",
    welcomeBack: (first: string) => `Welcome back, ${first}`,
    startFresh: (first: string) => `Not ${first}? Start fresh`,
    trackingBanner: "You have a request in progress",
    trackIt: "Track it",
    errors: {
      name: "Please add your name",
      phone: "Enter a valid mobile number",
      email: "Enter a valid email address",
      generic: "Something went wrong on our end. Please try again.",
    },
  },
  extras: {
    readingPill: "Reading your photos…",
    readDonePill: "Photos read",
    continue: "Continue",
    skip: "Skip",
    vehicle: {
      title: "Which car is it?",
      sub: "Optional. It helps the shop line up the right parts.",
      snapTile: "Snap the VIN sticker",
      snapSub: "Driver's door jamb or the base of the windshield",
      typeTile: "Type the VIN or plate",
      pickTile: "Pick year, make & model",
      reading: "Reading the sticker…",
      confirm: (label: string) => `Is it a ${label}?`,
      confirmYes: "Yes",
      confirmNo: "Not quite",
      readFailed: "Couldn't read it. Type it in or pick the model.",
      vinLabel: "VIN",
      vinHint: "17 characters, no I, O or Q",
      plateLabel: "License plate",
      plateHint: "We'll look it up at the shop",
      yearLabel: "Year",
      makeLabel: "Make",
      modelLabel: "Model",
      required: "The shop asks for the vehicle before you continue.",
    },
    situation: {
      title: "What happened?",
      sub: "A couple of taps saves the back-and-forth later.",
      drivableLabel: "Is it drivable?",
      causeLabel: "What caused it?",
      noteLabel: "Anything else? (optional)",
      notePlaceholder: "e.g. the trunk won't close right",
      dictate: "Dictate",
      dictateStop: "Stop",
      listening: "Listening…",
    },
    insurance: {
      title: "How are you paying?",
      sub: "Helps the shop start the paperwork. Skip it if you don't know yet.",
      carrierLabel: "Insurance company",
      carrierPlaceholder: "Pick your carrier",
      claimLabel: "Claim number (optional)",
      claimPlaceholder: "e.g. AB-1234567",
    },
    reach: {
      title: (shop: string) => `How should ${shop} reach you?`,
      channelLabel: "Best way to reach you",
      bestTimeLabel: "Best time",
      cta: "That's everything",
    },
  },
  reveal: {
    headline: (first: string | null) =>
      first ? `${first}, here's what we see` : "Here's what we see",
    chip: "Analysis complete",
    ctaGood: "Looks right, continue",
    ctaBad: "Doesn't look right? Add a photo",
    freeLine: (shop: string) =>
      `This is an early read from your photos. Free, no obligation. ${shop} confirms the exact work and price.`,
  },
  offer: {
    sent: (shopFirst: string) => `Sent to ${shopFirst}`,
    title: (first: string | null) => (first ? `${first}, you're all set.` : "You're all set."),
    body: (shopFirst: string, phone: string) => `${shopFirst} will text ${phone} shortly.`,
    inviteTitle: "Want your exact price sooner?",
    inviteBody: "Book a free inspection: a quick look in person, no obligation.",
    book: "Find a time",
    skip: "I'll wait to hear from them",
    done: "Done",
  },
  done: {
    title: "Sent to the shop.",
    titleBooked: "You're booked.",
    trackLine: "We've texted you a link to follow your repair.",
    trackCta: "Track your request",
  },
  fallback: {
    title: "Your photos are in",
    body: (shop: string, phone: string) =>
      `${shop} is reviewing them. We'll text ${phone} the moment your estimate is ready.`,
  },
}

export const INSURANCE_CARRIERS = [
  "State Farm",
  "GEICO",
  "Progressive",
  "Allstate",
  "USAA",
  "Liberty Mutual",
  "Farmers",
  "Nationwide",
  "Travelers",
  "Other",
] as const

